'use client'

import React from 'react'

import { css } from '@news/styled-system/css'
import type { PostFeatured } from '@/shared/newsdb'

import { LegalInfo } from './legal-info'
import { SidebarPost } from './post'
import styles from './view.module.css'

type FeedSidebarProps = {
    posts: PostFeatured[]
}

export function FeedSidebar({ posts }: FeedSidebarProps) {
    const sidebarRef = React.useRef<React.ComponentRef<'div'>>(null)
    const contentWrapperRef = React.useRef<React.ComponentRef<'div'>>(null)

    React.useLayoutEffect(() => {
        const handleScroll = () => {
            // NOTE: sidebar is hidden on narrow screens - no need to handle scroll
            if (window.innerWidth <= 1200) {
                return
            }

            if (contentWrapperRef.current === null || sidebarRef.current === null) {
                return
            }

            const scrolledPixels = window.scrollY
            const gap = 60
            const sidebarTop = sidebarRef.current.getBoundingClientRect().top + scrolledPixels - gap
            const sidebarContentHeight = contentWrapperRef.current.getBoundingClientRect().height
            const remainingSidebarOutsideViewport = sidebarContentHeight - (window.innerHeight + sidebarTop)

            if (scrolledPixels >= remainingSidebarOutsideViewport) {
                contentWrapperRef.current.style.transform = `translateY(-${remainingSidebarOutsideViewport}px)`
                contentWrapperRef.current.style.position = 'fixed'
            } else {
                // NOTE: move naturally with the rest of the page content when scrolling
                contentWrapperRef.current.style.transform = ''
                contentWrapperRef.current.style.position = ''
            }
        }

        window.addEventListener('scroll', handleScroll)

        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    return (
        <div
            ref={sidebarRef}
            className={css({
                width: '[330px]',
                backgroundColor: 'white',
                borderRight: '[1px solid #cccccc]',
                hideBelow: 'xl',
            })}
        >
            <div ref={contentWrapperRef} style={{ width: '330px' }}>
                {posts.map((post) => (
                    <div key={post.id} className={styles.container}>
                        <SidebarPost post={post} />
                    </div>
                ))}
                <div className={styles.container}>
                    <LegalInfo />
                </div>
            </div>
        </div>
    )
}
